import { clsx } from "clsx";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { Spinner } from "./Spinner";

const variantClasses = {
  primary:
    "bg-adara-700 text-white hover:bg-adara-800 focus-visible:ring-adara-500",
  secondary:
    "bg-adara-100 text-adara-700 hover:bg-adara-200 focus-visible:ring-adara-500",
  outline:
    "border border-edge bg-panel text-sub hover:bg-subtle hover:border-edge-hard focus-visible:ring-adara-500",
  ghost: "bg-transparent text-sub hover:bg-muted focus-visible:ring-adara-500",
  danger: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500",
} as const;

const sizeClasses = {
  sm: "h-8 px-3 text-[13px] gap-1.5",
  md: "h-9 px-3.5 text-sm gap-2",
  lg: "h-10 px-5 text-sm gap-2",
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variantClasses;
  size?: keyof typeof sizeClasses;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={clsx(
          "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md font-medium whitespace-nowrap",
          "transition-colors duration-150",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && "w-full",
          className,
        )}
        {...props}
      >
        {isLoading ? <Spinner size="sm" /> : leftIcon}
        {children}
        {!isLoading && rightIcon}
      </button>
    );
  },
);
